import React, { useCallback, useEffect } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import styled from 'styled-components';

import Modal from '../../../components/Modal';
import {
  setCurrentRepositActionModal,
  ADD_TENANT_ON_REPOSIT_STORE_KEY,
} from '../../../redux/reposit/reposit.actions';
import { Header3, P2 } from '../../../components/Typography';
import FieldWithLabel from '../../../components/FormFields/FieldWithLabel';
import { Input } from '../../../components/FormFields';
import Button from '../../../components/Button';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import { AppDispatch } from '../../..';
import { ConcurrentTenancyConflictPanel } from './ConcurrentTenancyConflictPanel';
import { useConcurrentTenancyCheck } from './useConcurrentTenancyCheck';
import { useAddTenantForm } from './useAddTenantForm';

const emailSchema = Yup.string().email();

interface AddTenantModalProps {
  tenancyId: string;
  organizationId: string;
  tenancyStartDate: string;
}

const AddTenantSchema = Yup.object().shape({
  newTenantEmail: Yup.string().email('Must be a valid email').required('Required'),
});

const POPPINS_FONT = '"Poppins", sans-serif';

const ModalContent = styled.div`
  font-family: ${POPPINS_FONT};
  color: ${(props) => props.theme.colors.body};
`;

const ActionBar = styled.div`
  display: flex;
  padding: 24px 0;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 10px;

  button {
    margin-right: 10px;
  }
`;


export const AddTenantModal: React.FC<AddTenantModalProps> = ({ tenancyId, organizationId, tenancyStartDate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSubmittingSelector = createLoadingSelector([ADD_TENANT_ON_REPOSIT_STORE_KEY]);
  const isSubmitting = useSelector(isSubmittingSelector);

  const {
    checkResult,
    checkError,
    checkedEmail,
    isChecking,
    checkConcurrentTenancy,
    clearCheck,
    addTenantDirectly,
  } = useConcurrentTenancyCheck({
    tenancyId,
    organizationId,
    tenancyStartDate,
  });

  const { handleFormSubmit, getButtonLabel } = useAddTenantForm({
    checkResult,
    checkError,
    checkedEmail,
    isChecking,
    checkConcurrentTenancy,
    addTenantDirectly,
  });

  const dismiss = useCallback(() => {
    dispatch(setCurrentRepositActionModal({ name: '' }));
    clearCheck();
  }, [dispatch, clearCheck]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      clearCheck();
    };
  }, [clearCheck]);

  return (
    <Modal onDismiss={dismiss}>
      <ModalContent>
        <Header3 style={{ marginBottom: 6 }}>Please enter the email for the new tenant </Header3>
        <P2 style={{ marginBottom: 24 }}>
          A concurrent tenancy check will be run automatically before adding the user to the reposit to identify any potential conflicts.
        </P2>
        <Formik
        initialValues={{ newTenantEmail: '' }}
        onSubmit={handleFormSubmit}
        validationSchema={AddTenantSchema}
      >
        {({ touched, errors, values, handleBlur, handleChange, handleSubmit }) => {
          const isValidEmail = emailSchema.isValidSync(values.newTenantEmail);
          const buttonLabel = getButtonLabel(values.newTenantEmail);

          return (
            <form onSubmit={handleSubmit}>
              <FieldWithLabel
                label="New tenant email address"
                style={{ marginBottom: 24 }}
                touched={touched.newTenantEmail}
                error={errors.newTenantEmail}
              >
                <Input
                  value={values.newTenantEmail}
                  onChange={(e) => {
                    handleChange(e);
                    const newEmail = e.target.value;
                    if (newEmail !== checkedEmail) {
                      clearCheck();
                    }
                  }}
                  onBlur={handleBlur}
                  name="newTenantEmail"
                  touched={touched.newTenantEmail}
                  error={errors.newTenantEmail}
                />
              </FieldWithLabel>

              {(checkError || (checkResult && checkedEmail === values.newTenantEmail)) && (
                <ConcurrentTenancyConflictPanel
                  checkResult={checkResult}
                  checkError={checkError}
                  checkedEmail={checkedEmail}
                />
              )}

              <ActionBar>
                <Button buttonType="secondary" onClick={dismiss}>
                  Cancel
                </Button>
                <Button disabled={isSubmitting || isChecking || !isValidEmail} type="submit">
                  {buttonLabel}
                </Button>
              </ActionBar>
            </form>
          );
        }}
      </Formik>
      </ModalContent>
    </Modal>
  );
};
